import React, { useContext, useState, useMemo } from 'react';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import PersonContext from '@/pages/Person/PersonContext';
import Button from '@/ui/components/Button';
import { hasPermission, PEOPLE_RUN_VOB } from '@/utils/userPermission';

import InsuranceVOB from '../InsuranceVOB';
import NewVOB from '../NewVOB';

import type { Props } from './types';

const VOBSection = ({ insurancePolicy }: Props) => {
  const {
    person: { first_name, last_name, birthday, id: personId }
  } = useContext(PersonContext);
  const { permissions } = useContext(AppContext);

  const [isVerifyingPolicy, setIsVerifyingPolicy] = useState(false);

  const reVerifyPolicy = () => setIsVerifyingPolicy(true);

  const dismissPolicyVerification = () => setIsVerifyingPolicy(false);

  const canRunVob = useMemo(() => {
    const requiredPermission = PEOPLE_RUN_VOB;
    return hasPermission({ permissions, requiredPermission });
  }, [permissions]);

  const { navigateToInsuranceVOBHistory, navigateToVOBList } = useNavigateToPage();

  if (isVerifyingPolicy) {
    return (
      <NewVOB
        insurancePolicy={insurancePolicy}
        dismissPolicyVerification={dismissPolicyVerification}
        firstName={first_name}
        lastName={last_name}
        birthday={birthday}
      />
    );
  }

  return (
    <>
      {insurancePolicy.last_verification ? (
        <>
          <InsuranceVOB
            headerText="Latest VOB"
            insuranceVerification={insurancePolicy.last_verification}
            onReVerifyPolicy={reVerifyPolicy}
            canRunVob={canRunVob && !insurancePolicy.is_self_pay}
          />
          <Button
            color={Color.WHITE}
            size="sm"
            onClick={() => navigateToInsuranceVOBHistory(personId, insurancePolicy)}
            className="w-full justify-center my-4">
            View all
          </Button>
          <Button
            color={Color.WHITE}
            size="sm"
            onClick={() => navigateToVOBList({ policy_id: insurancePolicy.id })}
            className="w-full justify-center mb-4">
            Open in VOB list
          </Button>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center">
          <p className="text-gray-600">
            {insurancePolicy.is_self_pay
              ? 'Self-pay policies are not verified with a payer'
              : 'No prior VoB found for this policy'}
          </p>
        </div>
      )}
    </>
  );
};
export default React.memo(VOBSection);
